import React, { Fragment, useState, useEffect, useRef, useMemo } from 'react';
import { Table, Button, message, Spin, DatePicker, Select } from 'antd';
import Card from '../../../components/bootstrap/card';
import { Col, Row } from 'react-bootstrap';
import { Excel } from 'antd-table-saveas-excel';
import { iecWiseAdvAuthDetailsService } from '../../../services/AdvanceAuthReportService';
import { getBRCorelationService, getReferenceDataService } from '../../../services/MasterService';
import { determineRemark } from '../utils/RemarkAllocation';
import { getReportSummaryData, getAllAdminIECsService, GetLicenseNoService } from '../../../services/FileDataService';
import { getTodaysDate, formatToTwoDecimals } from '../../../utilities/utils';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import dayjs from 'dayjs';
import { useLocation } from 'react-router-dom';

import useColumnVisibility from '../../../hooks/useColumnVisibility';
import ColumnSelector from '../../../components/common/ColumnSelector';

const { RangePicker } = DatePicker;

const AdvAuthDetailsReport = ({ data }) => {
  const location = useLocation();
  const reportType = 'ADV_AUTH';
  const [loading, setLoading] = useState(false);
  const [isLoading2, setIsLoading2] = useState(false);
  const [summaryDashboardData, setSummaryDashboardData] = useState([]);
  const [jsonData, setJsonData] = useState([]);
  const [error, setError] = useState(null);
  const [IECOptions, setIECOptions] = useState([]);
  const userRole = localStorage.getItem('role');
  const [selectedIEC, setSelectedIEC] = useState(
    localStorage.getItem('iec_no')
  );
  const [selectedIECName, setSelectedIECName] = useState(localStorage.getItem('name'))
  const today = new Date();
  const [isValid, setIsValid] = useState(true); // Validation state
  const [billNoOptions, setBillNoOptions] = useState([]); // Options for dropdown
  const [selectedBillNos, setSelectedBillNos] = useState([]); // Selected auth numbers
  const [branchCodeNames, setBranchCodeNames] = useState([]);
  const [selectedBranchCodes, setSelectedBranchCodes] = useState([]);

  const currentYear = dayjs().year();
  const defaultFrom = dayjs(`${currentYear}-01-01`);
  const defaultTo = dayjs(`${currentYear}-12-31`);
  const [dates, setDates] = useState(() => {
    // if report is opened through navigation from dashboard, then use those dates else default
    if (location.state?.passedDates?.length === 2) {
      return [
        dayjs(location.state.passedDates[0]),
        dayjs(location.state.passedDates[1]),
      ];
    }
    return [defaultFrom, defaultTo];
  });

  // Extract day, month, and year components
  const day = String(today.getDate()).padStart(2, '0');
  const month = String(today.getMonth() + 1).padStart(2, '0'); // January is 0!
  const year = today.getFullYear();
  const formattedDate = `${day}-${month}-${year}`;
  const tableStyles = `
    .adv-auth-summary-table .ant-table-thead > tr > th,
    .adv-auth-details-table .ant-table-thead > tr > th {
      white-space: normal !important;
      word-break: break-word !important;
      overflow: visible !important;
      text-overflow: unset !important;
      vertical-align: middle !important;
      line-height: 1.3 !important;
      padding-top: 8px !important;
      padding-bottom: 8px !important;
    }
    .adv-auth-summary-table .ant-table-tbody > tr > td,
    .adv-auth-details-table .ant-table-tbody > tr > td {
      white-space: nowrap;
    }
    .adv-auth-summary-table .ant-table-container,
    .adv-auth-details-table .ant-table-container {
      overflow-x: auto !important;
    }
    .adv-auth-summary-table .ant-table-header,
    .adv-auth-details-table .ant-table-header {
      overflow: hidden !important;
    }
    .adv-auth-summary-table .ant-table-sticky-holder,
    .adv-auth-details-table .ant-table-sticky-holder {
      overflow: hidden !important;
    }
  `;

  const getDateOnly = (dateString) => {
    if (dateString && dateString.length !== 0) {
      const date = new Date(dateString);
      return date;
    }
    else {
      return '';
    }
  }

  // Blanks out `fields` on a row when they're unchanged from the immediately
  // preceding row within the same `groupField` group (e.g. same Authorisation) —
  // display-only, so the underlying jsonData used for filtering/matching stays intact.
  const blankRepeats = (rows, fields, groupField = 'auth_number') => {
    return rows.map((row, i) => {
      if (i === 0) return row;
      const prevRow = rows[i - 1];
      if (row[groupField] !== prevRow[groupField]) return row;
      const out = { ...row };
      fields.forEach((f) => {
        if (row[f] === prevRow[f]) out[f] = '';
      });
      return out;
    });
  }

  const exportToExcel = async (filterRemark = null, record = null) => {
    let workbook = new ExcelJS.Workbook();
    let summaryWorksheet = workbook.addWorksheet('Summary');
    let worksheet = workbook.addWorksheet('Data');

    let data = jsonData;
    let summaryData = summaryDashboardData;

    // Apply filter if remark is clicked
    if (record) {
      // Add any keys here that are aggregated in the summary (e.g., amounts/totals) 
      // so they are not used for strict matching against raw data rows.
      const ignoreKeys = ['eo_fcc', 'eo_fulfilled_val', 'eo_fulfilled_realised_val', 'bal_eo_shb', 'bal_eo_brc', 'bal_eop'];

      // Generic filter: match all keys present in both the record and the item
      const isMatch = (item, searchRecord) => {
        for (let key in searchRecord) {
          if (key === 'key' || ignoreKeys.includes(key)) continue; // Skip React 'key' prop and aggregated keys
          if (item.hasOwnProperty(key)) {
            if (item[key] !== searchRecord[key]) {
              return false;
            }
          }
        }
        return true;
      };

      data = data.filter(item => isMatch(item, record));
      summaryData = summaryData.filter(item => isMatch(item, record));
    } else if (filterRemark && typeof filterRemark === 'string') {
      data = data.filter(item => item.final_remark === filterRemark);
      summaryData = summaryData.filter(item => item.final_remark === filterRemark);
    }

    data = blankRepeats(data, ['auth_number', 'business_unit', 'plant_name']);

    // Define columns for Excel export
    summaryWorksheet.columns = filteredSummaryColumns.map(col => ({
      header: col.header || col.title,
      key: col.dataIndex,
      width: 20
    }));
    // Apply styles to the header row (Row 1)
    summaryWorksheet.getRow(1).eachCell((cell) => {
      cell.font = { bold: true }; // Bold text
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFC000' }, // Yellow background
      };
      cell.alignment = { vertical: 'middle' }; // Center align
    });

    summaryData.forEach((obj) => {
      summaryWorksheet.addRow({
        ...obj,
        import_date: getDateOnly(obj.import_date),
      })
    })

    worksheet.columns = filteredColumns.map(col => ({
      header: col.header || col.title,
      key: col.dataIndex,
      width: 20
    }));
    // Apply styles to the header row (Row 1)
    worksheet.getRow(1).eachCell((cell) => {
      cell.font = { bold: true }; // Bold text
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFC000' }, // Yellow background
      };
      cell.alignment = { vertical: 'middle' }; // Center align
    });

    data.forEach((obj) => {
      worksheet.addRow({
        ...obj,
        import_date: getDateOnly(obj.import_date),
        sb_date: getDateOnly(obj.sb_date),
        export_obligation_period: getDateOnly(obj.export_obligation_period),
        date_of_realization: getDateOnly(obj.date_of_realization),
        egm_date: getDateOnly(obj.egm_date),
        fob_value: obj.fob_value ? parseFloat(obj.fob_value) : '',
        fob_for_value: obj.fob_for_value ? parseFloat(obj.fob_for_value) : '',
        quantity: obj.quantity ? parseFloat(obj.quantity) : '',
        expqty: obj.expqty ? parseFloat(obj.expqty) : '',
        value_difference: obj.value_difference ? parseFloat(obj.value_difference) : '',
        realised_value: obj.realised_value ? parseFloat(obj.realised_value) : '',
      })

    });

    // Generate Excel file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

    // Use FileSaver to save the file
    const fileNameSuffix = filterRemark && typeof filterRemark === 'string' ? `_${filterRemark}` : '';
    saveAs(blob, `Lucrative's CIP DSS - Advance Authorisation EO as on ${getTodaysDate()} of ${selectedIEC}${fileNameSuffix}.xlsx`);
  };

  const columns = [
    { title: 'BUSINESS UNIT', dataIndex: 'business_unit', key: 'business_unit', header: 'BUSINESS UNIT', width: 160 , fixed: 'left' },
    { title: 'PLANT NAME', dataIndex: 'plant_name', key: 'plant_name', header: 'PLANT NAME', width: 150 , fixed: 'left' },
    {
      title: 'ADV. AUTH. NO.',
      dataIndex: 'auth_number',
      key: 'auth_number',
      header: 'ADV. AUTH. NO.',
      width: 160,
    },
    {
      title: 'ADV. AUTH. DT.',
      dataIndex: 'import_date',
      key: 'import_date',
      header: 'ADV. AUTH. DT.',
      width: 160,
      render: (text, record) => {
        if (record.import_date) {
          return formatDateToDDMMYY(record.import_date);
        }
      }
    },
    {
      title: 'FY',
      dataIndex: 'fy',
      key: 'fy',
      header: 'FY',
      width: 100,
    },
    {
      title: 'EXPORT ITEM SR. NO. AS PER AUTH.',
      dataIndex: 'export_sr_no',
      key: 'export_sr_no',
      header: 'EXPORT ITEM SR. NO. AS PER AUTH.',
      align: 'right',
      width: 320,
    },
    {
      title: 'EO-DESCRIPTION',
      dataIndex: 'eo_description',
      key: 'eo_description',
      header: 'EO-DESCRIPTION',
      width: 200,
    },
    {
      title: 'EO-QTY.',
      dataIndex: 'quantity',
      key: 'quantity',
      header: 'EO-QTY.',
      align: 'right',
      width: 120,
    },
    {
      title: 'EO-UOM',
      dataIndex: 'uom',
      key: 'uom',
      header: 'EO-UOM',
      width: 100,
    },
    {
      title: 'EO-VALUE IN INR',
      dataIndex: 'fob_for_value',
      key: 'fob_for_value',
      header: 'EO-VALUE IN INR',
      align: 'right',
      width: 170,
      render: (text, record) => {
        return formatToTwoDecimals(text)
      }
    },
    {
      title: 'IEC/BR CODE',
      dataIndex: 'iec_br',
      key: 'iec_br',
      header: 'IEC/BR CODE',
      width: 140,
    },
    {
      title: 'PORT',
      dataIndex: 'port_code',
      key: 'port_code',
      header: 'PORT',
      width: 100,
    },
    {
      title: 'REF. CODE',
      dataIndex: 'reference_code',
      key: 'reference_code',
      header: 'REF. CODE',
      width: 120,
    },
    {
      title: 'SHB NO.',
      dataIndex: 'sb_no',
      key: 'sb_no',
      header: 'SHB NO.',
      width: 120,
    },
    {
      title: 'SHB DT.',
      dataIndex: 'sb_date',
      key: 'sb_date',
      header: 'SHB DT.',
      width: 120,
    },
    {
      title: 'HSN CODE',
      dataIndex: 'hs_cd',
      key: 'hs_cd',
      header: 'HSN CODE',
      width: 120,
    },
    {
      title: 'EXP. ITEM SR. NO.',
      dataIndex: 'exp_s_no',
      key: 'exp_s_no',
      header: 'EXP. ITEM SR. NO.',
      align: 'right',
      width: 190,
    },
    {
      title: 'SHB - DESCRIPTION',
      dataIndex: 'description',
      key: 'description',
      header: 'SHB - DESCRIPTION',
      width: 200,
    },
    {
      title: 'SHB-QTY.',
      dataIndex: 'expqty',
      key: 'expqty',
      header: 'SHB-QTY.',
      align: 'right',
      width: 120,
    },
    {
      title: 'SHB-VALUE IN INR',
      dataIndex: 'fob_value',
      key: 'fob_value',
      header: 'SHB-VALUE IN INR',
      align: 'right',
      width: 180,
      render: (text, record) => {
        return formatToTwoDecimals(text)
      }
    },
    {
      title: 'EOP DT.',
      dataIndex: 'export_obligation_period',
      key: 'export_obligation_period',
      header: 'EOP DT.',
      width: 130,
      render: (text, record) => {
        if (record.export_obligation_period) {
          return formatDateToDDMMYY(record.export_obligation_period);
        }
      },
    },
    {
      title: 'BRC NO.',
      dataIndex: 'brc_no',
      key: 'brc_no',
      header: 'BRC NO.',
      width: 130,
    },
    {
      title: 'DATE OF REALISATION',
      dataIndex: 'date_of_realization',
      key: 'date_of_realization',
      header: 'DATE OF REALISATION',
      width: 210,
      render: (text, record) => {
        if (record.date_of_realization) {
          return formatDateToDDMMYY(record.date_of_realization);
        }
      }
    },
    {
      title: 'VALUE',
      dataIndex: 'realised_value',
      key: 'realised_value',
      header: 'VALUE',
      align: 'right',
      width: 120,
      render: (text, record) => {
        return formatToTwoDecimals(text)
      }
    },
    {
      title: 'FCC',
      dataIndex: 'currency',
      key: 'currency',
      header: 'FCC',
      width: 100,
    },
    {
      title: 'PORT/FCC DIFF.',
      dataIndex: 'port_currency_difference',
      key: 'port_currency_difference',
      header: 'PORT/FCC DIFF.',
      width: 160,
    },
    {
      title: 'VALUE DIFF.',
      dataIndex: 'value_difference',
      key: 'value_difference',
      header: 'VALUE DIFF.',
      align: 'right',
      width: 140,
      render: (text, record) => {
        return formatToTwoDecimals(text)
      }
    },
    {
      title: 'RELEASED IN (DAYS)',
      dataIndex: 'payment_released_in_days',
      key: 'payment_released_in_days',
      header: 'RELEASED IN (DAYS)',
      align: 'right',
      width: 210,
    },
    {
      title: 'OBSERVATION',
      dataIndex: 'brc_remark',
      key: 'brc_remark',
      header: 'OBSERVATION',
      width: 180,
    },
    {
      title: 'FINAL REMARK',
      dataIndex: 'final_remark',
      key: 'final_remark',
      header: 'FINAL REMARK',
      width: 180,
    },
    {
      title: 'TEXTBOX 1',
      dataIndex: 'param1',
      key: 'param1',
      header: 'TEXTBOX 1',
      width: 120,
    },
    {
      title: 'TEXTBOX 2',
      dataIndex: 'param2',
      key: 'param2',
      header: 'TEXTBOX 2',
      width: 120,
    },
    {
      title: 'TEXTBOX 3',
      dataIndex: 'param3',
      key: 'param3',
      header: 'TEXTBOX 3',
      width: 120,
    },
    {
      title: 'TEXTBOX 4',
      dataIndex: 'param4',
      key: 'param4',
      header: 'TEXTBOX 4',
      width: 120,
    },
    {
      title: 'EGM No',
      dataIndex: 'egm_no',
      key: 'egm_no',
      header: 'EGM No',
      width: 130,
    },
    {
      title: 'EGM Dtd.',
      dataIndex: 'egm_date',
      key: 'egm_date',
      header: 'EGM Dtd.',
      width: 120,
      render: (text, record) => record.egm_date ? formatDateToDDMMYY(record.egm_date) : '',
    },
    {
      title: 'EGM Remarks',
      dataIndex: 'egm_remarks',
      key: 'egm_remarks',
      header: 'EGM Remarks',
      width: 140,
    },
  ];

  const { visibleKeys, setVisibleKeys, filteredColumns, resetToDefault } =
    useColumnVisibility('AdvanceAuthReport', columns);

  // Table display copy only — blanks repeats for the table's own rendering,
  // jsonData itself stays untouched for filtering/matching elsewhere.
  const displayData = useMemo(
    () => blankRepeats(jsonData, ['auth_number', 'business_unit', 'plant_name']),
    [jsonData]
  );

  const dashboardReportSummaryColumns = [
    {
      title: 'ADV. AUTH. NO.',
      dataIndex: 'auth_number',
      key: 'auth_number',
      header: 'ADV. AUTH. NO.',
      width: 160,
    },
    {
      title: 'ADV. AUTH DATE',
      dataIndex: 'import_date',
      key: 'import_date',
      header: 'ADV. AUTH DATE',
      width: 160,
    },
    {
      title: 'EXPORT ITEM SR. NO. AS PER AUTH.',
      dataIndex: 'export_sr_no',
      key: 'export_sr_no',
      header: 'EXPORT ITEM SR. NO. AS PER AUTH.',
      align: 'right',
      width: 320,
    },
    {
      title: 'EO-DESCRIPTION',
      dataIndex: 'eo_description',
      key: 'eo_description',
      header: 'EO-DESCRIPTION',
      width: 200,
    },
    {
      title: 'EO-QTY',
      dataIndex: 'eo_qty',
      key: 'eo_qty',
      header: 'EO-QTY',
      align: 'right',
      width: 120,
    },
    {
      title: 'EO-UOM',
      dataIndex: 'uom',
      key: 'uom',
      header: 'EO-UOM',
      width: 100,
    },
    {
      title: 'EO-VALUE IN INR',
      dataIndex: 'eo_value_in_fcc',
      key: 'eo_value_in_fcc',
      header: 'EO-VALUE IN INR',
      align: 'right',
      width: 170,
      render: (text, record) => {
        return formatToTwoDecimals(text)
      }
    },
    {
      title: 'EO-FULFILLED QTY',
      dataIndex: 'eo_fulfilled_qty',
      key: 'eo_fulfilled_qty',
      header: 'EO-FULFILLED QTY',
      align: 'right',
      width: 180,
    },
    {
      title: 'EO-FULFILLED VALUE',
      dataIndex: 'eo_fulfilled_val',
      key: 'eo_fulfilled_val',
      header: 'EO-FULFILLED VALUE',
      align: 'right',
      width: 200,
      render: (text, record) => {
        return formatToTwoDecimals(text)
      }
    },
    {
      title: 'BAL. EO - QTY',
      dataIndex: 'bal_eo_qty',
      key: 'bal_eo_qty',
      header: 'BAL. EO - QTY',
      align: 'right',
      width: 150,
    },
    {
      title: 'BAL. EO - VALUE',
      dataIndex: 'bal_eo_value',
      key: 'bal_eo_value',
      header: 'BAL. EO - VALUE',
      align: 'right',
      width: 170,
      render: (text, record) => {
        return formatToTwoDecimals(text)
      }
    },
    {
      title: 'BAL. EOP',
      dataIndex: 'bal_eop',
      key: 'bal_eop',
      header: 'BAL. EOP',
      align: 'right',
      width: 120,
    },
    {
      title: 'FINAL REMARK',
      dataIndex: 'final_remark',
      key: 'final_remark',
      header: 'FINAL REMARK',
      width: 180,
      render: (text, record) => (
        <span
          style={{ color: 'blue', cursor: 'pointer', textDecoration: 'underline' }}
          onClick={() => exportToExcel(text, record)}
        >
          {text}
        </span>
      ),
    }
  ];


  function formatDateToDDMMYY(dateString) {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return ''; // Return empty string if the dateString is not a valid date
    }
    else {
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = String(date.getFullYear()).slice(-2);
      return `${day}/${month}/${year}`;
    }

  }

  const getSummaryDashboard = async () => {
    const filters = {
      bill_no: selectedBillNos.length > 0 ? selectedBillNos : null,
      br_code: selectedBranchCodes.length > 0 ? selectedBranchCodes : null,
    };
    try {
      setIsLoading2(true)
      let reportTypes = 'AdvAuth-EO';
      const response = await getReportSummaryData(reportTypes, filters, selectedIEC);

      if (response.data) {
        setSummaryDashboardData(response.data);
      }
      else {
        console.log("getSummaryDashboard :: no reponse")
      }
      setIsLoading2(false)
    }
    catch (err) {
      setIsLoading2(false)
      console.log(err);
    }
  }

  const fetchData = async () => {
    const filters = {
      bill_no: selectedBillNos.length > 0 ? selectedBillNos : null,
      br_code: selectedBranchCodes.length > 0 ? selectedBranchCodes : null,
    };
    try {
      setLoading(true)
      const response = await iecWiseAdvAuthDetailsService(selectedIEC, filters);
      setJsonData(response);
    } catch (error) {
      setError(error.message);
      console.error('Error fetching data:', error);
      message.error(`Error fetching data: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Fetch license numbers immediately for the selected IEC
  const fetchLicenseNumbers = async (iec) => {
    const targetIEC = iec || selectedIEC;
    if (!targetIEC) return;
    try {
      const token = localStorage.getItem('token');
      const res = await GetLicenseNoService(token, targetIEC, 'ADV_AUTH');
      const list = res?.data || (Array.isArray(res) ? res : []);
      if (list.length > 0) {
        const options = list.map((item, index) => ({
          value: typeof item === 'object' ? (item.license_number || item.auth_number) : String(item),
          label: typeof item === 'object' ? (item.license_number || item.auth_number) : String(item),
          key: index,
        }));
        setBillNoOptions(options);
      }
    } catch (error) {
      console.error('Error fetching license numbers:', error);
    }
  };

  useEffect(() => {
    if (userRole === 'superadmin' || userRole === 'group_head') {
      getAdminIEC();
    }
    if (userRole === 'admin' || userRole === 'user') {
      fetchLicenseNumbers(selectedIEC);
      getBRCorelationService(selectedIEC).then((response) => {
        setBranchCodeNames(response);
      });
    }
  }, []);

  // Fetch license numbers when IEC changes
  useEffect(() => {
    if (selectedIEC) {
      setBillNoOptions([]);
      setSelectedBillNos([]);
      fetchLicenseNumbers(selectedIEC);
      getBRCorelationService(selectedIEC).then((response) => {
        setBranchCodeNames(response);
      });
    }
  }, [selectedIEC]);

  const handleDateChange = (value) => {
    setDates(value || []);
    if (value && value.length === 2) {
      const from_date = value[0];
      const to_date = value[1];

      // Validate that both dates are selected
      if (!from_date || !to_date) {
        setIsValid(false);
        return;
      }

      // Validate that the duration is within 1 year
      const oneYearLater = from_date.add(1, 'year');
      if (to_date.isAfter(oneYearLater)) {
        message.error("The selected duration should not exceed 1 year.");
        setIsValid(false);
        return;
      }

      setIsValid(true);
    } else {
      setIsValid(false);
    }
  };

  const handleSubmit = () => {
    if (!selectedIEC) {
      message.error("Please select an IEC");
      return;
    }

    fetchData();
    getSummaryDashboard();
    // if (dates.length === 2 && isValid) {
    //   fetchData();
    //   getSummaryDashboard();
    // } else {
    //   message.error("Please select a valid date range");
    // }
  };

  const getAdminIEC = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await getAllAdminIECsService(token);

      if (response?.data) {
        const options = response.data.map((item, index) => ({
          value: `${item.iec_no};${item.fullname}`,
          label: `${item.iec_no} (${item.fullname})`,
          key: index,
        }));
        setIECOptions(options);
      }
    } catch (err) {
      message.error("Failed to load IEC list");
    }
  };

  const handleIECSelection = (val) => {
    if (val) {
      const [iec_no, iec_name] = val.split(";");
      setSelectedIEC(iec_no);
      setSelectedIECName(iec_name);
    } else {
      setSelectedIEC(null);
      setSelectedIECName('');
      setBillNoOptions([]);
      setSelectedBillNos([]);
    }
  };

  const handleBranchCodeSelection = (values) => {
    setSelectedBranchCodes(values);
  };
  const preprocessData = (data) => {
    return data.map(record => {
      if (record.sb_date) {
        const sbDate = new Date(record.sb_date);
        // Extract fiscal year from sb_date
        // const fiscalYearStartYear = sbDate.getMonth() >= 3 ? sbDate.getFullYear() : sbDate.getFullYear() - 1;
        // const fiscalYearEndYear = fiscalYearStartYear + 1;
        // const fiscalYear = `AM${fiscalYearEndYear.toString().slice(-2)}`;
        // record.FY = fiscalYear;
        // Format sb_date

        record.sb_date = formatDateToDDMMYY(sbDate);
      } else {
        // record.FY = '';
        // record.sb_date = '';
        // record.sbDate = '';
      }

      return record;
    });
  };

  const filteredSummaryColumns = dashboardReportSummaryColumns.filter((col) => {
    const key = String(col.dataIndex || col.key);
    const isDefinedInDetail = columns.some((c) => String(c.dataIndex || c.key) === key);
    return isDefinedInDetail ? visibleKeys.includes(key) : true;
  });

  return (
    <Fragment>
      <style>{tableStyles}</style>
      <Row>
        <Col sm="12">
          <Card>
            <Card.Header className="d-flex justify-content-between">
              <div className="header-title">
                <h4 className="card-title">Lucrative's CIP DSS  -  Advance Authorization Status as on {getTodaysDate()} of {selectedIECName}</h4>
              </div>
              <div className="d-flex align-items-center gap-2">
                <ColumnSelector
                  allColumns={columns}
                  visibleKeys={visibleKeys}
                  setVisibleKeys={setVisibleKeys}
                  onReset={resetToDefault}
                />
                <button
                  className="btn btn-primary cip-btn-submit"
                  onClick={selectedBillNos.length === 0 ? undefined : exportToExcel}
                  style={{ cursor: (loading || selectedBillNos.length === 0) ? 'not-allowed' : 'pointer' }}
                >
                  Export to Excel
                </button>
              </div>
            </Card.Header>
            <Card.Body className="d-flex justify-content-start align-items-center gap-2 ps-4">
              {(userRole === 'superadmin' || userRole === 'group_head') && (
                <Select
                  showSearch
                  placeholder="Select IEC"
                  value={selectedIEC ? (IECOptions.find(opt => opt.value.split(';')[0] === selectedIEC)?.value || selectedIEC) : undefined}
                  onChange={handleIECSelection}
                  allowClear
                  options={IECOptions}
                  style={{ width: '300px' }}
                  filterOption={(input, option) =>
                    (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                  }
                />
              )}
              {/* <DatePicker.RangePicker value={dates} onChange={handleDateChange}
                allowClear
              /> */}
              <Select
                mode="multiple"
                showSearch
                placeholder="Select Auth No(s)"
                value={selectedBillNos}
                allowClear
                style={{ width: 280 }}
                filterOption={(input, option) =>
                  (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                }
                onChange={(vals) => setSelectedBillNos(vals || [])} // Update state when option(s) are selected
                options={billNoOptions} // Dynamically populated options
                disabled={!selectedIEC}
              />
              <Select
                mode="multiple"
                allowClear
                style={{ width: 250 }}
                placeholder="Select Branch Code"
                onChange={handleBranchCodeSelection}
                options={branchCodeNames.map((item) => ({
                  value: item.branch_code,
                  label: `${item.branch_code} / ${item.plant_name} (${item.business_unit})`,
                }))}
              />
              <Button type="primary" className="cip-btn-submit"
                style={{ cursor: selectedBillNos.length === 0 ? 'not-allowed' : 'pointer' }}
                onClick={selectedBillNos.length === 0 ? undefined : handleSubmit}>
                Submit
              </Button>
            </Card.Body>
            <Card.Body className="px-0 report-page">
              <Table
                className="adv-auth-summary-table"
                columns={filteredSummaryColumns}
                dataSource={summaryDashboardData}
                pagination={false}
                loading={isLoading2}
                scroll={{ x: 'max-content' }}
                
              />

              <hr />
              {loading ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <Spin size="large" />
                </div>
              ) : error ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <p>Error loading data: {error}</p>
                </div>
              ) : (
                <Table
                  className="adv-auth-details-table"
                  columns={filteredColumns}
                  dataSource={displayData}
                  pagination={{ showTotal: (total) => `Total ${total} records`, showSizeChanger: true }}
                  scroll={{ x: 'max-content' }}
                  
                />
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Fragment>
  );
};

export default AdvAuthDetailsReport;
